import { CommonModule, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, inject, input } from '@angular/core';
import { isEqual } from 'lodash-es';
import { Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import SwiperCore, { A11y, Autoplay, Navigation, Pagination, SwiperOptions } from 'swiper';
import { SwiperModule } from 'swiper/angular';

import {
  LARGE_BREAKPOINT_WIDTH,
  MEDIUM_BREAKPOINT_WIDTH,
  SMALL_BREAKPOINT_WIDTH,
} from 'ish-core/configurations/injection-keys';
import { ProductContextDirective } from 'ish-core/directives/product-context.directive';
import { ProductContextDisplayProperties } from 'ish-core/facades/product-context.facade';
import { ShoppingFacade } from 'ish-core/facades/shopping.facade';
import { InjectSingle } from 'ish-core/utils/injection';
import {
  ProductItemComponent,
  ProductItemDisplayType,
} from 'ish-shared/components/product/product-item/product-item.component';

SwiperCore.use([Autoplay, Pagination, Navigation, A11y]);

@Component({
  selector: 'ish-products-list',
  templateUrl: './products-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, SwiperModule, ProductContextDirective, NgClass, ProductItemComponent],
})
export class ProductsListComponent implements OnChanges {
  private smallBreakpointWidth = inject<InjectSingle<typeof SMALL_BREAKPOINT_WIDTH>>(SMALL_BREAKPOINT_WIDTH);
  private mediumBreakpointWidth = inject<InjectSingle<typeof MEDIUM_BREAKPOINT_WIDTH>>(MEDIUM_BREAKPOINT_WIDTH);
  private largeBreakpointWidth = inject<InjectSingle<typeof LARGE_BREAKPOINT_WIDTH>>(LARGE_BREAKPOINT_WIDTH);
  private shoppingFacade = inject(ShoppingFacade);

  readonly productSKUs = input.required<string[]>();
  readonly listStyle = input<string>(undefined);
  readonly slideItems = input<number>(undefined);
  readonly listItemStyle = input<ProductItemDisplayType>(undefined);
  readonly listItemCSSClass = input<string>(undefined);
  readonly listItemConfiguration = input<Partial<ProductContextDisplayProperties>>(undefined);
  productSKUs$: Observable<string[]>;

  /**
   * track already fetched SKUs
   */
  private fetchedSKUs = new Set<string>();

  /**
   * configuration of swiper carousel
   * https://swiperjs.com/swiper-api
   */
  swiperConfig: SwiperOptions;

  constructor() {
    this.swiperConfig = {
      watchSlidesProgress: true,
      direction: 'horizontal',
      navigation: true,
      pagination: {
        clickable: true,
      },
      observer: true,
      observeParents: true,
    };
  }

  ngOnChanges(): void {
    this.configureSlides(this.slideItems());
    // remove all SKUs from the productSKUs Array that are also contained in the failed products Array
    this.productSKUs$ = combineLatest([of(this.productSKUs()), this.shoppingFacade.failedProducts$]).pipe(
      distinctUntilChanged<[string[], string[]]>(isEqual),
      map(([skus, failed]) => skus.filter(x => !failed.includes(x)))
    );
  }

  lazyFetch(fetch: boolean, sku: string): boolean {
    if (fetch) {
      this.fetchedSKUs.add(sku);
    }
    return this.fetchedSKUs.has(sku);
  }

  /**
   * Configure Swiper slidesPerView/slidesPerGroup settings
   * with breakpoint responsive design considerations based on the given slide items.
   *
   * @param slideItems The amount of slide items that should be rendered if enough screen space is available.
   */
  configureSlides(slideItems: number) {
    switch (slideItems) {
      case 1: {
        this.swiperConfig.breakpoints = {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
        };
        break;
      }
      case 2: {
        this.swiperConfig.breakpoints = {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          [this.smallBreakpointWidth]: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
        };
        break;
      }
      case 3: {
        this.swiperConfig.breakpoints = {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          [this.smallBreakpointWidth]: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          [this.mediumBreakpointWidth]: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
        };
        break;
      }
      default: {
        this.swiperConfig.breakpoints = {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          [this.smallBreakpointWidth]: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          [this.mediumBreakpointWidth]: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
          [this.largeBreakpointWidth]: {
            slidesPerView: 4,
            slidesPerGroup: 4,
          },
        };
      }
    }
  }
}
